import { CollapsibleFilters } from '../../components/common/CollapsibleFilters';
// pages/hr/TeamDirectoryPage.tsx
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Checkbox,
  Box,
  Typography,
  Button,
  Input,
  Select,
  Option,
  Card,
  Chip,
  Avatar,
  IconButton,
  Dropdown,
  MenuButton,
  Menu,
  MenuItem,
  Modal,
  ModalDialog,
  ModalClose,
  FormControl,
  FormLabel,
  Stack,
  Divider,
} from '@mui/joy';
import { PageHelpButton } from '../../components/knowledge-base/PageHelpButton';
import { ResponsiveTable } from '../../components/common/ResponsiveTable';
import {
  MagnifyingGlass as SearchOutlined,
  Plus as AddOutlined,
  DotsThreeVertical as MoreVertOutlined,
  User as PersonOutlined,
  PencilSimple as EditOutlined,
  Eye as VisibilityOutlined,
  UserMinus as PersonOffOutlined,
  Copy as ContentCopyOutlined,
  CheckCircle as CheckCircleOutlined,
  ArrowSquareOut as OpenInNewOutlined,
  ArrowCounterClockwise as RehireOutlined,
} from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { useTeamMembers, useInviteNewHire, useGetOnboardingLink } from '../../hooks/useHr';
import { useDebounce } from '../../hooks/useDebounce';
import { apiClient } from '../../lib/api';
import { applyPrefill } from '../../utils/prefill';
import type { TeamMemberListItem, TeamMemberStatus, MemberType, InviteNewHireRequest } from '../../types/hr';
import { TeamMemberDetailDrawer } from '../../components/hr/TeamMemberDetailDrawer';
import { TeamMemberEditModal } from '../../components/hr/TeamMemberEditModal';
import { RehireModal } from '../../components/hr/RehireModal';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import { RosterToolbar, BulkRosterDialog, OffboardingDialog } from '../../components/hr/TeamWorkflowPanels';

const statusColors: Record<TeamMemberStatus, 'success' | 'warning' | 'danger' | 'neutral' | 'primary'> = {
  pending: 'warning',
  onboarding: 'primary',
  active: 'success',
  inactive: 'neutral',
  terminated: 'danger',
};

const statusLabels: Record<TeamMemberStatus, string> = {
  pending: 'Pending',
  onboarding: 'Onboarding',
  active: 'Active',
  inactive: 'Inactive',
  terminated: 'Terminated',
};

/**
 * The bits of /api/users-list/ the invite dialog needs.
 *
 * Declared here rather than reusing types/index.ts's User, which predates
 * `mobile_phone` - the field this dialog exists to stop retyping.
 */
interface OrgUser {
  id: number;
  first_name: string;
  last_name: string;
  full_name?: string;
  email: string;
  mobile_phone?: string;
}

const userLabel = (user: OrgUser): string =>
  user.full_name?.trim() || `${user.first_name} ${user.last_name}`.trim() || user.email;

// Ceiling on the user-list walk below, so a paging bug can never turn the
// invite dialog into an unbounded request loop.
const MAX_USER_PAGES = 25;

export const TeamDirectoryPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const [advanced, setAdvanced] = useState({ department: params.get('department') || '', job_title: params.get('job_title') || '', start_from: params.get('start_from') || '', start_to: params.get('start_to') || '' });
  const [selected, setSelected] = useState<string[]>([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [offboarding, setOffboarding] = useState<TeamMemberListItem | null>(null);
  const [search, setSearch] = useState(params.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<TeamMemberStatus | ''>((params.get('status') || '') as TeamMemberStatus);
  const [typeFilter, setTypeFilter] = useState<MemberType | ''>((params.get('member_type') || '') as MemberType);
  const [inviteModalOpen, setInviteModalOpen] = useState(params.get('invite') === '1');
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [inviteSuccessModalOpen, setInviteSuccessModalOpen] = useState(false);
  const [inviteSuccessData, setInviteSuccessData] = useState<{ name: string; email: string; url: string } | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rehireModalOpen, setRehireModalOpen] = useState(false);
  const [rehireMemberIds, setRehireMemberIds] = useState<string[]>([]);

  // Search and the filters below run server-side, so debounce keystrokes
  const debouncedSearch = useDebounce(search, 300);

  // Reset to page 1 when search/filters change - page 3 of the old result set
  // has nothing to do with the new one
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, typeFilter, advanced]);

  const rosterFilters = { search, status: statusFilter, member_type: typeFilter, ...advanced };
  useEffect(() => {
    setParams(Object.fromEntries(Object.entries(rosterFilters).filter(([, value]) => !!value)), { replace: true });
  }, [search, statusFilter, typeFilter, advanced, setParams]);
  const { data, isLoading, error: directoryError, refetch: retryDirectory } = useTeamMembers({
    ...advanced,
    search: debouncedSearch || undefined,
    status: statusFilter || undefined,
    member_type: typeFilter || undefined,
    page: currentPage,
  });
  const teamMembers = data?.results ?? [];
  const totalCount = data?.count ?? 0;
  const PAGE_SIZE = 20;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const inviteNewHire = useInviteNewHire();
  const getOnboardingLink = useGetOnboardingLink();

  const [inviteForm, setInviteForm] = useState<InviteNewHireRequest>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    member_type: 'w2',
    job_title: '',
    department: '',
  });

  // An org that ran on Settings → Users before adopting HR already has these
  // people as logins. Picking one fills the four identity fields and binds the
  // directory record to the account instead of creating a second identity.
  //
  // Every page is walked because /api/users-list/ returns 20 at a time and
  // ignores page_size: the orgs that need this picker are exactly the ones with
  // more logins than that, and a truncated list looks like "they aren't a user".
  const { data: orgUsers = [] } = useQuery({
    queryKey: ['organizationUsersAllPages'],
    queryFn: async () => {
      const collected: OrgUser[] = [];
      for (let page = 1; page <= MAX_USER_PAGES; page += 1) {
        const response = await apiClient.get<OrgUser[] | { next: string | null; results: OrgUser[] }>(
          `/api/users-list/?page=${page}`
        );
        const body = response.data;
        if (Array.isArray(body)) {
          collected.push(...body);
          break;
        }
        collected.push(...(body?.results || []));
        if (!body?.next) break;
      }
      return collected;
    },
    enabled: inviteModalOpen,
  });

  // What the picker last seeded, so picking a different person replaces its own
  // values while leaving anything HR typed by hand alone.
  const seededUserRef = useRef<Record<string, string> | null>(null);

  const handlePickExistingUser = (userId: number | null) => {
    const user = orgUsers.find((candidate) => candidate.id === userId);
    if (!user) {
      setInviteForm((prev) => ({ ...prev, user: undefined }));
      return;
    }

    const seeded = {
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      phone: user.mobile_phone || '',
    };
    const previous = seededUserRef.current;

    setInviteForm((prev) => {
      // Blank out what the picker itself put there so the new person's details
      // can take its place; anything else in the field is HR's own typing.
      const ours = (field: 'first_name' | 'last_name' | 'email' | 'phone') =>
        !prev[field] || prev[field] === previous?.[field];
      return applyPrefill(
        {
          ...prev,
          user: user.id,
          first_name: ours('first_name') ? '' : prev.first_name,
          last_name: ours('last_name') ? '' : prev.last_name,
          email: ours('email') ? '' : prev.email,
          phone: ours('phone') ? '' : prev.phone,
        },
        seeded
      );
    });
    seededUserRef.current = seeded;
  };

  const openRehireModal = (memberIds: string[]) => {
    setRehireMemberIds(memberIds);
    setRehireModalOpen(true);
  };

  const handleInvite = async () => {
    try {
      const result = await inviteNewHire.mutateAsync(inviteForm);
      const fullUrl = window.location.origin + result.onboarding_url;

      setInviteModalOpen(false);

      // Show success modal with onboarding link
      setInviteSuccessData({
        name: `${inviteForm.first_name} ${inviteForm.last_name}`,
        email: inviteForm.email,
        url: fullUrl,
      });
      setLinkCopied(false);
      setInviteSuccessModalOpen(true);

      setInviteForm({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        member_type: 'w2',
        job_title: '',
        department: '',
      });
      seededUserRef.current = null;
    } catch (error: unknown) {
      // Typing last season's crew into the invite dialog is how a spring hiring
      // run starts. The backend answers with the existing record instead of a
      // dead end, so hand straight over to the rehire flow.
      const conflict = (error as {
        status?: number;
        responseData?: {
          error?: string;
          can_rehire?: boolean;
          existing_member?: { id: string; full_name: string };
        };
      });
      if (conflict?.status === 409 && conflict.responseData?.existing_member) {
        const existing = conflict.responseData.existing_member;
        setInviteModalOpen(false);
        if (conflict.responseData.can_rehire) {
          openRehireModal([existing.id]);
          toast(`${existing.full_name} has worked here before — rehiring instead`);
        } else {
          toast.error(conflict.responseData.error || 'That team member already exists');
        }
        return;
      }
      const errorMessage = error instanceof Error ? error.message : 'Failed to send invitation';
      toast.error(errorMessage);
    }
  };

  const handleCopyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      toast.success('Onboarding link copied to clipboard');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleGetOnboardingLink = async (member: TeamMemberListItem) => {
    try {
      const result = await getOnboardingLink.mutateAsync(member.id);
      const fullUrl = window.location.origin + result.onboarding_url;

      setInviteSuccessData({
        name: member.full_name,
        email: member.email,
        url: fullUrl,
      });
      setLinkCopied(false);
      setInviteSuccessModalOpen(true);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'This link is expired or unavailable. Resend it from Onboarding.');
    }
  };

  const handleTerminate = (member: TeamMemberListItem) => setOffboarding(member);

  const handleViewDetails = (memberId: string) => {
    setSelectedMemberId(memberId);
    setDetailDrawerOpen(true);
  };

  const handleEditMember = (memberId: string) => {
    setSelectedMemberId(memberId);
    setEditModalOpen(true);
  };

  const handleEditFromDrawer = () => {
    setDetailDrawerOpen(false);
    setEditModalOpen(true);
  };

  const handleEditSuccess = () => {
    toast.success('Team member updated successfully');
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: 3, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' } }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography level="h2" sx={{ fontSize: { xs: '1.5rem', md: undefined } }}>Team Directory</Typography>
            <PageHelpButton />
          </Box>
          <Typography level="body-sm" color="neutral">
            Invite a new hire, review their profile, or manage an existing team member.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<RehireOutlined />}
            onClick={() => openRehireModal([])}
          >
            Rehire Crew
          </Button>
          <Button
            startDecorator={<AddOutlined />}
            onClick={() => setInviteModalOpen(true)}
          >
            Invite New Hire
          </Button>
        </Stack>
      </Stack>

      <RosterToolbar filters={rosterFilters} onLoad={value => { setSearch(value.search || ''); setStatusFilter((value.status || '') as TeamMemberStatus); setTypeFilter((value.member_type || '') as MemberType); setAdvanced({ department: value.department || '', job_title: value.job_title || '', start_from: value.start_from || '', start_to: value.start_to || '' }); }} onAdvanced={setAdvanced} selectedCount={selected.length} onBulk={() => setBulkOpen(true)} />
      {bulkOpen && <BulkRosterDialog open ids={selected} onClose={() => setBulkOpen(false)} />}
      {offboarding && <OffboardingDialog member={offboarding} onClose={() => setOffboarding(null)} />}
      {/* Filters */}
      <CollapsibleFilters><Card sx={{ mb: 3, p: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          flexWrap="wrap"
          useFlexGap
        >
          <Input
            placeholder="Search team members..." aria-label="Search team members"
            startDecorator={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: { xs: 0, sm: 250 }, flex: { xs: '1 1 100%', sm: '1 1 250px' } }}
          />
          <Select
            placeholder="Status" aria-label="Employment status"
            value={statusFilter}
            onChange={(_, value) => setStatusFilter(value as TeamMemberStatus | '')}
            sx={{ minWidth: { xs: 0, sm: 150 }, flex: { xs: '1 1 calc(50% - 6px)', sm: '0 0 150px' } }}
          >
            <Option value="">All Statuses</Option>
            <Option value="active">Active</Option>
            <Option value="onboarding">Onboarding</Option>
            <Option value="pending">Pending</Option>
            <Option value="inactive">Inactive</Option>
            <Option value="terminated">Terminated</Option>
          </Select>
          <Select
            placeholder="Type" aria-label="Employment type"
            value={typeFilter}
            onChange={(_, value) => setTypeFilter(value as MemberType | '')}
            sx={{ minWidth: { xs: 0, sm: 150 }, flex: { xs: '1 1 calc(50% - 6px)', sm: '0 0 150px' } }}
          >
            <Option value="">All Types</Option>
            <Option value="w2">W-2 Employee</Option>
            <Option value="1099">1099 Contractor</Option>
          </Select>
        </Stack>
      </Card></CollapsibleFilters>

      {/* Team Members Table */}
      {directoryError ? <Alert color="danger" endDecorator={<Button onClick={() => retryDirectory()}>Retry</Button>}>Could not load the team directory. Your team has not been removed.</Alert> : isLoading ? (
        <Card>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography level="body-md">Loading…</Typography>
          </Box>
        </Card>
      ) : teamMembers.length === 0 ? (
        <Card>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <PersonOutlined size={48} color="var(--joy-palette-neutral-400)" style={{ marginBottom: 8 }} />
            <Typography level="body-lg">No team members found</Typography>
            <Typography level="body-sm" color="neutral">
              {debouncedSearch || statusFilter || typeFilter
                ? 'No team members match these filters'
                : 'Click "Invite New Hire" to add your first team member'}
            </Typography>
          </Box>
        </Card>
      ) : (
        <Card sx={{ p: { xs: 0, md: 0 } }}>
          <ResponsiveTable<TeamMemberListItem>
            rows={teamMembers}
            getRowKey={(m) => m.id}
            columns={[
              { key: 'select', header: 'Select', render: member => <Checkbox slotProps={{ input: { 'aria-label': `Select ${member.full_name}` } }} checked={selected.includes(member.id)} onChange={e => setSelected(ids => e.target.checked ? [...ids, member.id] : ids.filter(id => id !== member.id))} /> },
              {
                key: 'name',
                sortValue: (row) => row.last_name ?? row.full_name,
                header: 'Name',
                mobileVariant: 'header',
                render: (member) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                    <Avatar
                      src={member.profile_photo || undefined}
                      alt={member.full_name}
                      size="sm"
                    >
                      {member.first_name[0]}{member.last_name[0]}
                    </Avatar>
                    <Typography level="title-sm" sx={{ minWidth: 0, wordBreak: 'break-word' }}>
                      {member.full_name}
                    </Typography>
                  </Box>
                ),
              },
              {
                key: 'email',
                sortValue: (row) => row.email,
                header: 'Email',
                render: (member) => (
                  <Typography level="body-sm" sx={{ wordBreak: 'break-all' }}>
                    {member.email}
                  </Typography>
                ),
              },
              {
                key: 'type',
                sortValue: (row) => row.member_type,
                header: 'Type',
                render: (member) => (
                  <Chip size="sm" variant="soft">
                    {member.member_type === 'w2' ? 'W-2' : '1099'}
                  </Chip>
                ),
              },
              {
                key: 'job_title',
                sortValue: (row) => row.job_title,
                header: 'Job Title',
                render: (member) => <Typography level="body-sm">{member.job_title || '-'}</Typography>,
              },
              {
                key: 'department',
                sortValue: (row) => row.department,
                header: 'Department',
                render: (member) => <Typography level="body-sm">{member.department || '-'}</Typography>,
              },
              {
                key: 'status',
                header: 'Status',
                render: (member) => (
                  <Chip size="sm" color={statusColors[member.status]} variant="soft">
                    {statusLabels[member.status]}
                  </Chip>
                ),
              },
            ]}
            rowActions={(member) => (
              <Dropdown>
                <MenuButton aria-label={`Actions for ${member.full_name}`}
                  slots={{ root: IconButton }}
                  slotProps={{ root: { variant: 'plain', size: 'sm' } }}
                >
                  <MoreVertOutlined />
                </MenuButton>
                <Menu placement="bottom-end">
                  <MenuItem onClick={() => handleViewDetails(member.id)}>
                    <VisibilityOutlined style={{ marginRight: 8 }} />
                    View Details
                  </MenuItem>
                  <MenuItem onClick={() => handleEditMember(member.id)}>
                    <EditOutlined style={{ marginRight: 8 }} />
                    Edit
                  </MenuItem>
                  {(member.status === 'pending' || member.status === 'onboarding') && (
                    <MenuItem onClick={() => handleGetOnboardingLink(member)}>
                      <ContentCopyOutlined style={{ marginRight: 8 }} />
                      Copy Onboarding Link
                    </MenuItem>
                  )}
                  {(member.status === 'terminated' || member.status === 'inactive') && (
                    <MenuItem onClick={() => openRehireModal([member.id])}>
                      <RehireOutlined style={{ marginRight: 8 }} />
                      Rehire
                    </MenuItem>
                  )}
                  {member.status !== 'terminated' && <Divider />}
                  {member.status !== 'terminated' && (
                    <MenuItem
                      color="danger"
                      onClick={() => handleTerminate(member)}
                    >
                      <PersonOffOutlined style={{ marginRight: 8 }} />
                      Terminate
                    </MenuItem>
                  )}
                </Menu>
              </Dropdown>
            )}
          />
        </Card>
      )}

      {/* Pagination */}
      {totalCount > 0 && (
        <Box
          sx={{
            mt: 3,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 1.5,
          }}
        >
          <Typography level="body-sm" color="neutral">
            {`Showing ${Math.min((currentPage - 1) * PAGE_SIZE + 1, totalCount)}–${Math.min(currentPage * PAGE_SIZE, totalCount)} of ${totalCount} team members`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(p => p - 1)}
              sx={{ flex: { xs: 1, sm: 'none' } }}
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(p => p + 1)}
              sx={{ flex: { xs: 1, sm: 'none' } }}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}

      {/* Invite New Hire Modal */}
      <Modal open={inviteModalOpen} onClose={() => setInviteModalOpen(false)}>
        <ModalDialog sx={{ width: { xs: 'auto', sm: 500 }, maxHeight: '90vh', overflowY: 'auto' }}>
          <ModalClose />
          <Typography level="h4">Invite New Hire</Typography>
          <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
            Send an onboarding invitation to a new team member
          </Typography>

          <Stack spacing={2}>
            {orgUsers.length > 0 && (
              <FormControl>
                <FormLabel>Existing Login (Optional)</FormLabel>
                <Select
                  value={inviteForm.user ?? null}
                  onChange={(_, value) => handlePickExistingUser(value as number | null)}
                  placeholder="Not an existing ApexOS user"
                >
                  <Option value={null}>Not an existing ApexOS user</Option>
                  {orgUsers.map((user) => (
                    <Option key={user.id} value={user.id}>
                      {userLabel(user)} — {user.email}
                    </Option>
                  ))}
                </Select>
              </FormControl>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>First Name *</FormLabel>
                <Input
                  value={inviteForm.first_name}
                  onChange={(e) => setInviteForm({ ...inviteForm, first_name: e.target.value })}
                  placeholder="John"
                />
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Last Name *</FormLabel>
                <Input
                  value={inviteForm.last_name}
                  onChange={(e) => setInviteForm({ ...inviteForm, last_name: e.target.value })}
                  placeholder="Doe"
                />
              </FormControl>
            </Box>

            <FormControl>
              <FormLabel>Email *</FormLabel>
              <Input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                placeholder="john.doe@example.com"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Phone</FormLabel>
              <Input
                value={inviteForm.phone || ''}
                onChange={(e) => setInviteForm({ ...inviteForm, phone: e.target.value })}
                placeholder="(555) 123-4567"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Employment Type *</FormLabel>
              <Select
                value={inviteForm.member_type}
                onChange={(_, value) => setInviteForm({ ...inviteForm, member_type: value as MemberType })}
              >
                <Option value="w2">W-2 Employee</Option>
                <Option value="1099">1099 Contractor</Option>
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Job Title</FormLabel>
                <Input
                  value={inviteForm.job_title || ''}
                  onChange={(e) => setInviteForm({ ...inviteForm, job_title: e.target.value })}
                  placeholder="Sales Representative"
                />
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Department</FormLabel>
                <Input
                  value={inviteForm.department || ''}
                  onChange={(e) => setInviteForm({ ...inviteForm, department: e.target.value })}
                  placeholder="Sales"
                />
              </FormControl>
            </Box>

            <Stack
              direction={{ xs: 'column-reverse', sm: 'row' }}
              spacing={1.5}
              sx={{ justifyContent: 'flex-end', mt: 2 }}
            >
              <Button variant="outlined" color="neutral" onClick={() => setInviteModalOpen(false)} fullWidth={true} sx={{ flex: { sm: '0 0 auto' } }}>
                Cancel
              </Button>
              <Button
                onClick={handleInvite}
                loading={inviteNewHire.isPending}
                disabled={!inviteForm.first_name || !inviteForm.last_name || !inviteForm.email}
                fullWidth={true}
                sx={{ flex: { sm: '0 0 auto' } }}
              >
                Send Invitation
              </Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* Team Member Detail Drawer */}
      <TeamMemberDetailDrawer
        open={detailDrawerOpen}
        onClose={() => setDetailDrawerOpen(false)}
        memberId={selectedMemberId}
        onEdit={handleEditFromDrawer}
      />

      {/* Rehire Modal - single from the row menu, or the whole crew at once */}
      <RehireModal
        open={rehireModalOpen}
        onClose={() => setRehireModalOpen(false)}
        initialMemberIds={rehireMemberIds}
        onSuccess={(count) =>
          toast.success(
            `${count} team member${count === 1 ? '' : 's'} rehired — onboarding started`
          )
        }
      />

      {/* Team Member Edit Modal */}
      <TeamMemberEditModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        memberId={selectedMemberId}
        onSuccess={handleEditSuccess}
      />

      {/* Onboarding Link Success Modal */}
      <Modal
        open={inviteSuccessModalOpen}
        onClose={() => setInviteSuccessModalOpen(false)}
      >
        <ModalDialog sx={{ width: { xs: 'auto', sm: 500 }, maxHeight: '90vh', overflowY: 'auto' }}>
          <ModalClose />
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <CheckCircleOutlined size={48} color="var(--joy-palette-success-500)" style={{ marginBottom: 8 }} />
            <Typography level="h4">Invitation Sent!</Typography>
          </Box>

          {inviteSuccessData && (
            <Stack spacing={2}>
              <Typography level="body-md" textAlign="center">
                An onboarding invitation has been sent to{' '}
                <strong>{inviteSuccessData.name}</strong> ({inviteSuccessData.email}).
              </Typography>

              <Divider />

              <Box>
                <Typography level="title-sm" sx={{ mb: 1 }}>
                  Onboarding Link
                </Typography>
                <Typography level="body-sm" color="neutral" sx={{ mb: 1 }}>
                  Share this link directly with the new hire to access their onboarding:
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    bgcolor: 'neutral.100',
                    borderRadius: 'sm',
                    border: '1px solid',
                    borderColor: 'neutral.300',
                  }}
                >
                  <Typography
                    level="body-sm"
                    sx={{
                      flex: 1,
                      fontFamily: 'monospace',
                      wordBreak: 'break-all',
                      fontSize: '12px',
                    }}
                  >
                    {inviteSuccessData.url}
                  </Typography>
                  <IconButton
                    size="sm"
                    variant={linkCopied ? 'soft' : 'outlined'}
                    color={linkCopied ? 'success' : 'neutral'}
                    onClick={() => handleCopyLink(inviteSuccessData.url)}
                  >
                    {linkCopied ? <CheckCircleOutlined /> : <ContentCopyOutlined />}
                  </IconButton>
                </Box>
              </Box>

              <Stack
                direction={{ xs: 'column-reverse', sm: 'row' }}
                spacing={1}
                sx={{ justifyContent: 'center', mt: 2 }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  startDecorator={<OpenInNewOutlined />}
                  onClick={() => window.open(inviteSuccessData.url, '_blank')}
                >
                  Open Link
                </Button>
                <Button
                  variant={linkCopied ? 'soft' : 'solid'}
                  color={linkCopied ? 'success' : 'primary'}
                  startDecorator={linkCopied ? <CheckCircleOutlined /> : <ContentCopyOutlined />}
                  onClick={() => handleCopyLink(inviteSuccessData.url)}
                >
                  {linkCopied ? 'Copied!' : 'Copy Link'}
                </Button>
              </Stack>
            </Stack>
          )}
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default TeamDirectoryPage;
